import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const Font = Quill.import("formats/font");
Font.whitelist = ["sans-serif", "serif", "monospace"];
Quill.register(Font, true);

export function RichTextEditor({ value, onChange }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const lastHtmlRef = useRef(value || "");

  useEffect(() => {
    if (!containerRef.current) return;
    if (quillRef.current) return;

    const q = new Quill(containerRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ color: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          [{ font: Font.whitelist }],
          ["link"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["clean"],
        ],
      },
    });

    // prevent the editor from expanding and blocking other inputs in modals
    q.root.style.minHeight = "140px";
    q.root.style.maxHeight = "260px";
    q.root.style.overflowY = "auto";

    quillRef.current = q;

    // initial value
    if (value) {
      q.clipboard.dangerouslyPasteHTML(value);
      lastHtmlRef.current = value;
    }

    q.on("text-change", () => {
      const html = q.root.innerHTML;
      lastHtmlRef.current = html;
      onChange?.(html);
    });

    // no cleanup needed; component lives in modal/form
    // (Quill doesn't expose an official destroy API in v1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = quillRef.current;
    if (!q) return;
    const next = value || "";
    if (next === lastHtmlRef.current) return;

    const sel = q.getSelection();
    q.clipboard.dangerouslyPasteHTML(next);
    lastHtmlRef.current = next;
    if (sel) q.setSelection(sel);
  }, [value]);

  return <div data-testid="rich-text-editor" ref={containerRef} />;
}
